import {
  Dialog,
  Button,
  Box,
  TextField,
  Snackbar,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
  Paper,
  TableSortLabel,
  IconButton,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import ClearIcon from "@mui/icons-material/Clear";
import React, { useEffect, useMemo, useState } from "react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ja } from "date-fns/locale";
import { ShipmentDialogStyle as styles } from "./ShipmentDialogStyle";
import { useApiUpdateAmazonItems } from "../hooks/api/useApiUpdateAmazonItems";
import {
  RequestGetListingsItemsRequest,
  ResponseListingsItemResponse,
  ResponseListingsItemsResult,
} from "../services/OpenAPI";
import { useRecoilValue } from "recoil";
import { loginUserSelector } from "../recoil/selectors/loginUserSelector";
import { useApiGetListingsItems } from "../hooks/api/useApiGetListingsItems";

interface Props {
  open: boolean;
  onClose: () => void;
  listingsItems: ResponseListingsItemsResult;
  setListingsItems: React.Dispatch<
    React.SetStateAction<ResponseListingsItemsResult | null>
  >;
  onSelect?: (item: ResponseListingsItemResponse) => void;
  initialKeyword?: string;
}

// -----------------------------
// 固定定義
// -----------------------------

type ListingsItemKey = keyof ResponseListingsItemResponse;

type Header = {
  label: string;
  key: ListingsItemKey;
  width?: string | number;
};

// ヘッダー定義
const headers: Header[] = [
  { label: "商品写真", key: "mainImageUrl", width: 80 },
  { label: "商品名", key: "itemName", width: 20 },
  { label: "ASIN", key: "asin", width: 40 },
  { label: "SKU", key: "sku", width: 30 },
  { label: "FNSKU", key: "fnsku", width: 40 },
  { label: "JAN", key: "jan", width: 40 },
];

const TABLE_HEADER_FONT_SIZE = "0.65rem";
const TABLE_BODY_FONT_SIZE = "1rem";
const TABLE_ROW_HEIGHT = 110;

// -----------------------------
// メインコンポーネント
// -----------------------------

const ListingsItemDialog: React.FC<Props> = ({
  open,
  listingsItems,
  setListingsItems,
  onClose,
  onSelect,
  initialKeyword = "",
}) => {
  const maxItemsPerPage = 20;

  useApiUpdateAmazonItems();
  const loginUser = useRecoilValue(loginUserSelector);
  const { getListingsItems, isLoading: isListingsItemsLoading } =
    useApiGetListingsItems();

  // 検索キーワード
  const [filterKeyword, setFilterKeyword] = useState("");
  const [localListingsItems, setLocalListingsItems] =
    useState<ResponseListingsItemsResult>(listingsItems);

  useEffect(() => {
    if (open && listingsItems) {
      setLocalListingsItems(listingsItems);
    }
  }, [open, listingsItems]);

  const [pageIndex, setPageIndex] = useState(0);
  const totalCount = localListingsItems?.numberOfResults ?? 0;
  const start = pageIndex * maxItemsPerPage + 1;
  const end = Math.min(start + maxItemsPerPage - 1, totalCount);
  const [toast, setToast] = useState<{
    msg: string;
    ok: boolean | null;
  } | null>(null);
  const handleCloseToast = () => setToast(null);

  useEffect(() => {
    if (open) setFilterKeyword(initialKeyword);
  }, [open, initialKeyword]);

  const displayedItems = useMemo(() => {
    if (!localListingsItems || !localListingsItems.items) return [];
    if (!filterKeyword.trim()) return localListingsItems.items;

    const kw = filterKeyword.trim().toLowerCase();
    return localListingsItems.items.filter((it) =>
      [
        it.itemName,
        it.asin,
        it.sku,
        it.fnsku,
        Array.isArray(it.jan) ? it.jan.join(" ") : it.jan,
      ]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(kw))
    );
  }, [filterKeyword, localListingsItems]);

  const onPageChange = async (
    pageToken: string,
    direction: "next" | "prev"
  ) => {
    if (!loginUser) return;
    const req: RequestGetListingsItemsRequest = {
      userId: loginUser.id,
      // pageToken: encodeURIComponent(pageToken),
      pageToken: pageToken,
    };
    const result = await getListingsItems(req);
    if (result) {
      setLocalListingsItems(result);
      setPageIndex((prev) =>
        direction === "next" ? prev + 1 : Math.max(0, prev - 1)
      );
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
      <Dialog
        open={open}
        onClose={(e, reason) => {
          if (reason !== "backdropClick") {
            setPageIndex(0);
            onClose();
          }
        }}
        disableEnforceFocus
        maxWidth={false}
        fullWidth={false}
        PaperProps={{
          sx: {
            display: "flex",
            width: "calc(100vw - 40px)",
            height: "calc(100vh - 40px)",
            maxWidth: "none",
            maxHeight: "none",
            overflowY: "auto",
          },
        }}
        sx={{ zIndex: 5001 }}
      >
        {/* ---- ヘッダー ---- */}
        <Box sx={styles.header}>
          <Box sx={{ display: "inline-block", textAlign: "center" }}>
            <TextField
              size="small"
              placeholder="商品名・ASIN・SKU・FNSKU・JAN を検索"
              value={filterKeyword}
              onChange={(e) => setFilterKeyword(e.target.value)}
              sx={{ width: 380, mr: 2, backgroundColor: "#fff" }}
              InputProps={{
                endAdornment: filterKeyword && (
                  <IconButton size="small" onClick={() => setFilterKeyword("")}>
                    <ClearIcon />
                  </IconButton>
                ),
              }}
            />
          </Box>
          {/* <Box fontSize="0.9rem">
            全{totalCount}件
          </Box> */}
          <Button
            onClick={() => {
              setPageIndex(0);
              setListingsItems(null);
              onClose();
            }}
            sx={styles.closeButton}
          >
            ×
          </Button>
        </Box>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          px={2}
          gap={1}
        >
          <Box>
            {localListingsItems && localListingsItems.previousToken && (
              <Button
                onClick={() =>
                  onPageChange(localListingsItems.previousToken!, "prev")
                }
                variant="outlined"
                size="small"
                sx={{ mr: 1 }}
              >
                ◀
              </Button>
            )}
            {localListingsItems && localListingsItems.nextToken && (
              <Button
                onClick={() =>
                  onPageChange(localListingsItems.nextToken!, "next")
                }
                variant="outlined"
                size="small"
              >
                ▶
              </Button>
            )}
          </Box>
          {/* <Box fontSize="0.9rem">
            {start}～{end}件表示 / 全{totalCount}件中
          </Box> */}
        </Box>

        <Box display="flex" alignItems="center" gap={1} mb={2} px={2}>
          <TableContainer
            component={Paper}
            sx={{
              // maxHeight: `${TABLE_ROW_HEIGHT * 4.5}px`,
              height: `calc(100vh - 130px)`,
              overflowY: "auto",
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    {/* <Checkbox
                        checked={
                          checkedIds.size > 0 && checkedIds.size === data.length
                        }
                        indeterminate={
                          checkedIds.size > 0 && checkedIds.size < data.length
                        }
                        onChange={(e) => {
                          e.stopPropagation();
                          handleSelectAll();
                        }}
                      /> */}
                  </TableCell>
                  {headers.map((header) => (
                    <TableCell
                      key={String(header.key)}
                      sx={{
                        fontSize: TABLE_HEADER_FONT_SIZE,
                        px: 0,
                        width: header.width,
                      }}
                    >
                      <TableSortLabel>{header.label}</TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedItems?.map((item) => (
                  <TableRow
                    key={item.sku}
                    sx={{
                      // backgroundColor:
                      //   item.rowColor === "yellow"
                      //     ? "#fff59d"
                      //     : item.rowColor === "green"
                      //     ? "#81c784"
                      //     : "inherit",
                      fontSize: TABLE_BODY_FONT_SIZE,
                      height: `${TABLE_ROW_HEIGHT}px`,
                    }}
                  >
                    <TableCell
                      padding="checkbox"
                      sx={{ fontSize: TABLE_BODY_FONT_SIZE }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* <Checkbox
                      // checked={checkedIds.has(String(item.id))}
                      // onChange={(e) => {
                      //   e.stopPropagation();
                      //   handleParentCheck(String(item.id));
                      // }}
                      /> */}
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => {
                          setPageIndex(0);
                          onSelect?.(item);
                        }}
                      >
                        選択
                      </Button>
                    </TableCell>
                    {headers.map((header) => {
                      const value = item[header.key];
                      const align =
                        typeof value === "number" ? "right" : "left";

                      return (
                        <TableCell
                          key={String(header.key)}
                          align={align}
                          sx={{
                            fontSize: TABLE_BODY_FONT_SIZE,
                            width: header.width,
                          }}
                        >
                          {
                            // header.key === "fnsku" &&
                            // typeof value === "string" ? (
                            //   <BarcodeCanvas value={value} />
                            // ) :
                            header.key === "jan" && Array.isArray(value) ? (
                              <Box whiteSpace="pre-line">
                                {value.join("\n")}
                              </Box>
                            ) : header.key === "mainImageUrl" ? (
                              <img
                                src={value as string | undefined}
                                alt="商品画像"
                                style={{
                                  width: 100,
                                  height: 100,
                                }}
                              />
                            ) : (
                              value
                            )
                          }
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Backdrop
            open={isListingsItemsLoading}
            sx={{
              zIndex: (theme) => theme.zIndex.drawer + 100,
              color: "#fff",
            }}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        </Box>
      </Dialog>
      {/* トースト */}
      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert
          onClose={handleCloseToast}
          severity={toast?.ok ? "success" : "error"}
          elevation={6}
          variant="filled"
        >
          {toast?.msg}
        </MuiAlert>
      </Snackbar>
    </LocalizationProvider>
  );
};

export default ListingsItemDialog;
